// @ts-nocheck
/**
 * ツインキーパッシブマネージャー
 *
 * 仕様書: twin_key_passive_system_v1.md
 * 設計書: twin_key_phase2_design.md
 */

import {
  PASSIVE_SLOTS,
  PASSIVE_TRIGGER_TYPES,
  PASSIVE_EFFECT_TYPES,
  PASSIVE_UNLOCK_REQUIREMENTS,
  PASSIVE_BASE_VALUES,
  isPassiveUnlocked,
  getUnlockedSlots,
} from './passiveTypes';
import {
  ATTRIBUTE_PASSIVE_TENDENCY,
  CATEGORY_PASSIVE_TENDENCY,
  generatePassiveTemplate,
  getMatrixEntry,
} from './passiveMatrix';

/**
 * パッシブマネージャークラス
 *
 * 責務:
 * - パッシブ解放状態の管理
 * - パッシブ発動判定
 * - パッシブ効果の計算
 * - 味方支援パッシブの適用
 */
class PassiveManager {
  constructor() {
    // パッシブマネージャーはステートレス設計
    // 使用回数などはモンスターインスタンス側で管理
  }

  /**
   * モンスターの解放済みパッシブを取得
   *
   * @param {Object} monster - モンスター
   * @returns {Array<Object>} 解放済みパッシブ一覧
   */
  getActivePassives(monster) {
    const awakening = monster.awakening || 0;
    const passives = monster.passives || [];

    return passives.filter((passive) => {
      const required = PASSIVE_UNLOCK_REQUIREMENTS[passive.slot];
      return awakening >= required;
    });
  }

  /**
   * 特定トリガーで発動するパッシブを取得
   *
   * @param {Object} monster - モンスター
   * @param {string} triggerType - トリガータイプ
   * @param {Object} context - 発動コンテキスト
   * @returns {Array<Object>} 発動可能なパッシブ一覧
   */
  getTriggeredPassives(monster, triggerType, context = {}) {
    const activePassives = this.getActivePassives(monster);

    return activePassives.filter((passive) => {
      // トリガータイプチェック
      if (passive.trigger !== triggerType) return false;

      // 使用回数チェック
      if (passive.usesPerBattle !== undefined) {
        const usedCount = monster.passiveUsage?.[passive.slot] || 0;
        if (usedCount >= passive.usesPerBattle) return false;
      }

      // 条件チェック
      if (passive.condition) {
        if (!this.checkCondition(passive.condition, monster, context)) {
          return false;
        }
      }

      // 確率チェック
      if (passive.probability !== undefined) {
        if (Math.random() > passive.probability) return false;
      }

      return true;
    });
  }

  /**
   * 条件をチェック
   *
   * @param {Object} condition - 条件オブジェクト
   * @param {Object} monster - パッシブ所有モンスター
   * @param {Object} context - コンテキスト
   * @returns {boolean}
   */
  checkCondition(condition, monster, context) {
    const { type, target, value } = condition;

    switch (type) {
      case 'HP_BELOW': {
        const targetMonster = target === 'self' ? monster : context.targetMonster;
        if (!targetMonster) return false;
        const hpPercent = targetMonster.currentHp / (targetMonster.maxHp || targetMonster.hp);
        return hpPercent <= value;
      }

      case 'HP_ABOVE': {
        const targetMonster = target === 'self' ? monster : context.targetMonster;
        if (!targetMonster) return false;
        const hpPercent = targetMonster.currentHp / (targetMonster.maxHp || targetMonster.hp);
        return hpPercent >= value;
      }

      case 'ALLY_COUNT': {
        const allyCount = context.allies?.filter((a) => a && a.currentHp > 0).length || 0;
        return allyCount >= value;
      }

      case 'ENEMY_COUNT': {
        const enemyCount = context.enemies?.filter((e) => e && e.currentHp > 0).length || 0;
        return enemyCount >= value;
      }

      case 'ALLY_ATTRIBUTE': {
        return context.allies?.some((a) => a && a.attribute === value) || false;
      }

      case 'ENEMY_ATTRIBUTE': {
        return context.enemies?.some((e) => e && e.attribute === value) || false;
      }

      case 'TURN_NUMBER': {
        return (context.turnNumber || 0) >= value;
      }

      case 'FIRST_ACTION': {
        return context.isFirstAction === true;
      }

      case 'MY_TURN': {
        return context.isMyTurn === true;
      }

      case 'normal_attack': {
        return context.actionType === 'normal_attack';
      }

      case 'ally_defeated': {
        return context.allyDefeated === true;
      }

      case 'self_defeated': {
        return monster.currentHp <= 0;
      }

      default:
        return true;
    }
  }

  /**
   * パッシブ使用をマーク
   */
  markPassiveUsed(monster, slot) {
    const passiveUsage = { ...(monster.passiveUsage || {}) };
    passiveUsage[slot] = (passiveUsage[slot] || 0) + 1;
    return { ...monster, passiveUsage };
  }

  /**
   * パッシブ使用回数をリセット（戦闘開始時）
   */
  resetPassiveUsage(monster) {
    return { ...monster, passiveUsage: {} };
  }

  // ==================== 効果計算メソッド ====================

  /**
   * 常時パッシブによるステータス補正を計算
   *
   * @param {Object} monster - モンスター
   * @param {Array<Object>} allies - 味方一覧（味方支援パッシブ用）
   * @returns {{ atkPercent, hpPercent, defFlat, spdFlat, critRate }}
   */
  calculateStatModifiers(monster, allies = []) {
    const modifiers = {
      atkPercent: 0,
      hpPercent: 0,
      defFlat: 0,
      spdFlat: 0,
      critRate: 0,
    };

    // 自身の常時パッシブ
    const selfPassives = this.getTriggeredPassives(monster, PASSIVE_TRIGGER_TYPES.ALWAYS);
    for (const passive of selfPassives) {
      this.applyPassiveToModifiers(passive, modifiers);
    }

    // 味方支援パッシブ
    for (const ally of allies) {
      if (!ally || ally.id === monster.id) continue;
      const supportPassives = this.getTriggeredPassives(ally, PASSIVE_TRIGGER_TYPES.ALLY_SUPPORT);
      for (const passive of supportPassives) {
        if (passive.target === 'allies') {
          this.applyPassiveToModifiers(passive, modifiers);
        }
      }
    }

    return modifiers;
  }

  /**
   * パッシブ効果をモディファイアに適用
   * @private
   */
  applyPassiveToModifiers(passive, modifiers) {
    const effect = passive.effect;
    const value = passive.value || passive.values?.percent || 0;

    switch (effect) {
      case PASSIVE_EFFECT_TYPES.ATK_PERCENT:
        modifiers.atkPercent += value;
        break;
      case PASSIVE_EFFECT_TYPES.HP_PERCENT:
        modifiers.hpPercent += value;
        break;
      case PASSIVE_EFFECT_TYPES.DEF_FLAT:
        modifiers.defFlat += passive.values?.flat || value;
        break;
      case PASSIVE_EFFECT_TYPES.SPD_FLAT:
        modifiers.spdFlat += passive.values?.flat || value;
        break;
      case PASSIVE_EFFECT_TYPES.CRIT_RATE:
        modifiers.critRate += value;
        break;
    }
  }

  /**
   * 与ダメージ補正を計算
   *
   * @param {Object} monster - 攻撃モンスター
   * @param {Object} context - コンテキスト
   * @returns {number} ダメージ倍率 (1.0 = 100%)
   */
  getDamageDealtMultiplier(monster, context = {}) {
    let multiplier = 1.0;

    // 行動時パッシブ
    const actionPassives = this.getTriggeredPassives(
      monster,
      PASSIVE_TRIGGER_TYPES.ON_ACTION,
      context
    );

    for (const passive of actionPassives) {
      if (passive.effect === PASSIVE_EFFECT_TYPES.DAMAGE_DEALT_PERCENT) {
        multiplier += passive.value || 0;
      }
    }

    return multiplier;
  }

  /**
   * 被ダメージ補正を計算
   *
   * @param {Object} monster - 被弾モンスター
   * @param {Object} context - コンテキスト
   * @returns {number} ダメージ倍率 (1.0 = 100%)
   */
  getDamageReceivedMultiplier(monster, context = {}) {
    let multiplier = 1.0;

    // 常時パッシブ
    const alwaysPassives = this.getTriggeredPassives(monster, PASSIVE_TRIGGER_TYPES.ALWAYS);
    for (const passive of alwaysPassives) {
      if (passive.effect === PASSIVE_EFFECT_TYPES.DAMAGE_RECEIVED_PERCENT) {
        multiplier += passive.value || 0;
      }
    }

    // 適応パッシブ（連続攻撃ダメージ減少）
    const onDamagePassives = this.getTriggeredPassives(
      monster,
      PASSIVE_TRIGGER_TYPES.ON_DAMAGE_RECEIVED,
      context
    );
    for (const passive of onDamagePassives) {
      if (passive.effect === PASSIVE_EFFECT_TYPES.ADAPT) {
        const consecutiveHits = context.consecutiveHitsFromSameEnemy || 0;
        multiplier -= passive.value * consecutiveHits;
      }
    }

    return Math.max(0.1, multiplier); // 最低10%
  }

  /**
   * 回復効果補正を計算
   *
   * @param {Object} monster - 回復対象モンスター
   * @returns {number} 回復倍率 (1.0 = 100%)
   */
  getHealMultiplier(monster) {
    let multiplier = 1.0;

    const passives = this.getTriggeredPassives(monster, PASSIVE_TRIGGER_TYPES.ALWAYS);
    for (const passive of passives) {
      if (passive.effect === PASSIVE_EFFECT_TYPES.HEAL_PERCENT) {
        multiplier += passive.value || 0;
      }
    }

    return multiplier;
  }

  /**
   * ダメージ上限をチェック
   *
   * @param {Object} monster - 被弾モンスター
   * @param {number} damage - 元ダメージ
   * @returns {number} 上限適用後ダメージ
   */
  applyDamageCap(monster, damage) {
    const passives = this.getTriggeredPassives(monster, PASSIVE_TRIGGER_TYPES.ALWAYS);

    for (const passive of passives) {
      if (passive.effect === PASSIVE_EFFECT_TYPES.DAMAGE_CAP) {
        const maxDamage = Math.floor((monster.maxHp || monster.hp) * passive.value);
        damage = Math.min(damage, maxDamage);
      }
    }

    return damage;
  }

  /**
   * 防御無視率を取得
   *
   * @param {Object} monster - 攻撃モンスター
   * @returns {number} 防御無視率 (0.0 ~ 1.0)
   */
  getDefenseIgnoreRate(monster) {
    let ignoreRate = 0;

    const passives = this.getTriggeredPassives(monster, PASSIVE_TRIGGER_TYPES.ALWAYS);
    for (const passive of passives) {
      if (passive.effect === PASSIVE_EFFECT_TYPES.IGNORE_DEF) {
        ignoreRate += passive.value || 0;
      }
    }

    return Math.min(1.0, ignoreRate);
  }

  /**
   * 先制パッシブを持っているか
   *
   * @param {Object} monster - モンスター
   * @returns {boolean}
   */
  hasFirstStrike(monster) {
    const passives = this.getTriggeredPassives(monster, PASSIVE_TRIGGER_TYPES.ALWAYS);
    return passives.some((p) => p.effect === PASSIVE_EFFECT_TYPES.FIRST_STRIKE);
  }

  /**
   * 状態異常耐性をチェック
   *
   * @param {Object} monster - モンスター
   * @param {string} statusType - 状態異常タイプ
   * @returns {boolean} 完全耐性があるか
   */
  hasStatusImmunity(monster, statusType) {
    const passives = this.getTriggeredPassives(monster, PASSIVE_TRIGGER_TYPES.ALWAYS);

    for (const passive of passives) {
      if (passive.effect === PASSIVE_EFFECT_TYPES.STATUS_RESIST) {
        if (passive.value >= 1.0) {
          // 完全耐性
          if (!passive.statusTypes || passive.statusTypes.includes(statusType)) {
            return true;
          }
        }
      }
    }

    return false;
  }

  /**
   * 属性弱点無効を持っているか
   *
   * @param {Object} monster - モンスター
   * @returns {boolean}
   */
  hasWeakImmunity(monster) {
    const passives = this.getTriggeredPassives(monster, PASSIVE_TRIGGER_TYPES.ALWAYS);
    return passives.some((p) => p.effect === 'WEAK_IMMUNITY');
  }

  // ==================== イベント処理メソッド ====================

  /**
   * 戦闘開始時パッシブを処理
   *
   * @param {Object} monster - モンスター
   * @returns {{ monster: Object, effects: Array<Object> }}
   */
  processBattleStart(monster) {
    const passives = this.getTriggeredPassives(monster, PASSIVE_TRIGGER_TYPES.BATTLE_START);
    const effects = [];
    let updatedMonster = { ...monster };

    for (const passive of passives) {
      const result = this.executePassiveEffect(passive, updatedMonster);
      updatedMonster = result.monster;
      effects.push({
        passive,
        ...result,
      });

      // 使用回数をマーク
      if (passive.usesPerBattle) {
        updatedMonster = this.markPassiveUsed(updatedMonster, passive.slot);
      }
    }

    return { monster: updatedMonster, effects };
  }

  /**
   * 撃破時パッシブを処理
   *
   * @param {Object} monster - モンスター
   * @param {Object} context - コンテキスト
   * @returns {{ monster: Object, effects: Array<Object> }}
   */
  processOnKill(monster, context) {
    const passives = this.getTriggeredPassives(monster, PASSIVE_TRIGGER_TYPES.ON_KILL, context);
    const effects = [];
    let updatedMonster = { ...monster };

    for (const passive of passives) {
      const result = this.executePassiveEffect(passive, updatedMonster, context);
      updatedMonster = result.monster;
      effects.push({
        passive,
        ...result,
      });
    }

    return { monster: updatedMonster, effects };
  }

  /**
   * ターン終了時パッシブを処理
   *
   * @param {Object} monster - モンスター
   * @returns {{ monster: Object, effects: Array<Object> }}
   */
  processTurnEnd(monster) {
    const passives = this.getTriggeredPassives(monster, PASSIVE_TRIGGER_TYPES.TURN_END);
    const effects = [];
    let updatedMonster = { ...monster };

    for (const passive of passives) {
      const result = this.executePassiveEffect(passive, updatedMonster);
      updatedMonster = result.monster;
      effects.push({
        passive,
        ...result,
      });
    }

    return { monster: updatedMonster, effects };
  }

  /**
   * パッシブ効果を実行
   * @private
   */
  executePassiveEffect(passive, monster, context = {}) {
    const { effect, value, source } = passive;
    let updatedMonster = { ...monster };
    const messages = [];

    switch (effect) {
      case PASSIVE_EFFECT_TYPES.HP_RECOVER: {
        const maxHp = monster.maxHp || monster.hp;
        let healAmount = 0;

        if (source === 'max_hp') {
          healAmount = Math.floor(maxHp * value);
        } else if (source === 'damage_dealt') {
          healAmount = Math.floor((context.damageDealt || 0) * value);
        } else {
          healAmount = Math.floor(maxHp * value);
        }

        updatedMonster.currentHp = Math.min(maxHp, updatedMonster.currentHp + healAmount);
        messages.push(`${monster.name}は${healAmount}回復した！`);
        break;
      }

      case PASSIVE_EFFECT_TYPES.SHIELD: {
        const maxHp = monster.maxHp || monster.hp;
        const shieldAmount = Math.floor(maxHp * value);
        updatedMonster.shield = (updatedMonster.shield || 0) + shieldAmount;
        messages.push(`${monster.name}に${shieldAmount}のシールドが付与された！`);
        break;
      }

      case PASSIVE_EFFECT_TYPES.INVINCIBLE: {
        updatedMonster.invincibleTurns = (updatedMonster.invincibleTurns || 0) + (passive.duration || 1);
        messages.push(`${monster.name}は無敵状態になった！`);
        break;
      }

      case PASSIVE_EFFECT_TYPES.SURVIVE: {
        updatedMonster.hasGuts = true;
        messages.push(`${monster.name}は根性で耐えた！`);
        break;
      }

      case PASSIVE_EFFECT_TYPES.REVIVE: {
        const maxHp = monster.maxHp || monster.hp;
        updatedMonster.currentHp = Math.floor(maxHp * value);
        messages.push(`${monster.name}は復活した！`);
        break;
      }

      case PASSIVE_EFFECT_TYPES.BUFF_APPLY: {
        // バフマネージャーと連携
        if (passive.buffType) {
          updatedMonster.pendingBuffs = updatedMonster.pendingBuffs || [];
          updatedMonster.pendingBuffs.push({
            type: passive.buffType,
            stack: passive.stack || 1,
            duration: passive.duration || 3,
          });
          messages.push(`${monster.name}にバフが付与された！`);
        }
        break;
      }

      case PASSIVE_EFFECT_TYPES.COUNTER_ATTACK: {
        const counterDamage = Math.floor((monster.attack || 0) * value);
        messages.push(`${monster.name}は反撃で${counterDamage}ダメージを与えた！`);
        return { monster: updatedMonster, messages, counterDamage };
      }

      default:
        break;
    }

    return { monster: updatedMonster, messages };
  }

  // ==================== パッシブ生成 ====================

  /**
   * モンスター用パッシブセットを生成
   *
   * @param {Object} card - カードデータ
   * @returns {Array<Object>} パッシブ一覧
   */
  generatePassivesForCard(card) {
    const { attribute, category } = card;
    const passives = [];

    // 各スロットのパッシブを生成
    for (const slot of Object.values(PASSIVE_SLOTS)) {
      const template = generatePassiveTemplate(attribute, category || 'ヒューマノイド', slot);
      passives.push({
        ...template,
        cardId: card.id,
      });
    }

    return passives;
  }

  /**
   * パッシブサマリーを取得（UI表示用）
   *
   * @param {Object} monster - モンスター
   * @returns {Array<Object>}
   */
  getPassiveSummary(monster) {
    const awakening = monster.awakening || 0;
    const passives = monster.passives || [];

    return passives.map((passive) => {
      const required = PASSIVE_UNLOCK_REQUIREMENTS[passive.slot];
      const unlocked = awakening >= required;

      return {
        slot: passive.slot,
        name: passive.name || `パッシブ${passive.slot}`,
        description: passive.description || '',
        unlocked,
        requiredAwakening: required,
        trigger: passive.trigger,
        usesPerBattle: passive.usesPerBattle,
        usedCount: monster.passiveUsage?.[passive.slot] || 0,
      };
    });
  }
}

// シングルトンインスタンス
export const passiveManager = new PassiveManager();
export default PassiveManager;
